import React from "react";
import { Box, Typography, Paper, alpha, useTheme } from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";

const DashboardStatsCard = ({
  title,
  value,
  icon: Icon,
  gradient,
  trend,
  trendValue,
  subtitle,
  description,
  status,
  onClick,
}) => {
  const theme = useTheme();

  const handleClick = () => {
    if (onClick && status) {
      onClick(status);
    }
  };

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        bgcolor: "white",
        border: "1px solid",
        borderColor: alpha(gradient[0], 0.3),
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 12px 24px ${alpha(gradient[0], 0.2)}`,
        cursor: onClick && status ? "pointer" : "default",
        transition: "all 0.3s ease",
        "&:hover":
          onClick && status
            ? {
                transform: "translateY(-4px)",
                boxShadow: `0 16px 32px ${alpha(gradient[0], 0.3)}`,
              }
            : {},
      }}
    >
      {/* Background Decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${alpha(gradient[0], 0.1)}, ${alpha(gradient[1], 0.05)})`,
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Icon and Title */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.text.primary, 0.6),
                fontWeight: 500,
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: theme.palette.text.primary,
                fontSize: "2rem",
                letterSpacing: "-1px",
                lineHeight: 1,
              }}
            >
              {value}
            </Typography>
          </Box>

          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
              boxShadow: `0 4px 12px ${alpha(gradient[0], 0.3)}`,
            }}
          >
            <Icon sx={{ fontSize: 24, color: "white" }} />
          </Box>
        </Box>

        {/* Trend and Subtitle */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {description && (
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.text.primary, 0.7),
                fontSize: "0.8rem",
                fontWeight: 600,
                mb: subtitle ? 0.5 : 0,
              }}
            >
              {description}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: alpha(theme.palette.text.primary, 0.5),
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </Typography>
            )}

            {trend && trendValue && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor:
                    trend === "up"
                      ? alpha("#10b981", 0.1)
                      : alpha("#ef4444", 0.1),
                  px: 1,
                  py: 0.5,
                  borderRadius: "6px",
                }}
              >
                {trend === "up" ? (
                  <TrendingUp sx={{ fontSize: 14, color: "#10b981" }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 14, color: "#ef4444" }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: trend === "up" ? "#10b981" : "#ef4444",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                  }}
                >
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default DashboardStatsCard;
